// src/pages/AddShopMemberPage.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShopStore } from '../../store/slices/shopSlice';
import { 
    useSearchProfiles, 
    useAddShopMember, 
    useShopMembers, 
    useRemoveShopMember 
} from '../../hooks/useShops';
import toast from 'react-hot-toast';
import { 
    FaUserPlus, 
    FaTrashAlt, 
    FaSearch, 
    FaUserTag, 
    FaSpinner, 
    FaStore, 
    FaTimes,
    FaCrown, 
    FaUserShield,
    FaArrowLeft,
    FaPhone,
    FaCopy,
    FaCheck,
    FaUser
} from 'react-icons/fa';

// --- Confirmation Modal Component ---
const ConfirmationModal = ({ isOpen, onCancel, onConfirm, title, message, confirmText = 'Confirm', isPending = false }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-auto transform transition-all scale-100 ease-out duration-300">
                <div className="p-5 md:p-6">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 md:mb-4 border-b pb-2 flex items-center text-red-600">
                        <FaTimes color="#DC2626" size={18} className="mr-2" /> {title}
                    </h3>
                    <p className="text-sm md:text-base text-gray-700 mb-5 md:mb-6">{message}</p>
                    <div className="flex flex-col-reverse sm:flex-row justify-end sm:space-x-3 gap-2 sm:gap-0">
                        <button
                            onClick={onCancel}
                            className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition"
                            disabled={isPending}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={onConfirm}
                            className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition disabled:bg-red-300 flex items-center justify-center"
                            disabled={isPending}
                        >
                            {isPending && <FaSpinner color="#ffffff" size={16} className="animate-spin mr-2" />}
                            {isPending ? 'Processing...' : confirmText}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- Main Page Component ---
const AddShopMemberPage = () => {
    const navigate = useNavigate();
    const { currentShop } = useShopStore();
    const shopId = currentShop?.id;
    const shopName = currentShop?.name || 'Shop Management';
    const shopLogoUrl = currentShop?.shop_logo_url;
    const shopType = currentShop?.shop_type?.en; 

    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [newMemberRole, setNewMemberRole] = useState('manager');
    const [copiedPhoneId, setCopiedPhoneId] = useState(null);
    
    // State for the removal modal
    const [isRemovalModalOpen, setIsRemovalModalOpen] = useState(false);
    const [memberToRemove, setMemberToRemove] = useState(null); 

    // Fetch existing members
    const { 
        data: members, 
        isLoading: isLoadingMembers, 
        isError: isMembersError, 
        error: membersError 
    } = useShopMembers(shopId);

    // Search for users
    const { data: profilesData, isFetching: isSearchingProfiles } = useSearchProfiles(userSearchTerm);
    
    // Mutations
    const { mutate: addMember, isPending: isAddingMember } = useAddShopMember();
    const { mutate: removeMember, isPending: isRemovingMember } = useRemoveShopMember();

    // Back Navigation Logic
    const handleBack = () => {
        if (window.history.state && window.history.state.idx > 0) {
            navigate(-1);
        } else {
            navigate('/', { replace: true });
        }
    };

    // Copy Phone Number Handler
    const handleCopyPhone = (phone, memberId) => {
        if (!phone) return;
        navigator.clipboard.writeText(phone);
        toast.success('Phone number copied!');
        setCopiedPhoneId(memberId);
        setTimeout(() => setCopiedPhoneId(null), 2000);
    };

    if (!shopId) {
        return (
            <div className="max-w-6xl mx-auto p-4 md:p-8 mt-6 md:mt-10 text-center text-red-600 bg-red-50 border border-red-200 rounded-xl m-4">
                <p className="text-sm md:text-base">⚠️ **Error:** No shop is currently selected. Please log in to a shop first.</p>
            </div>
        );
    }

    const profiles = (userSearchTerm.length >= 2 && profilesData) ? profilesData : [];

    // --- Member Addition Logic ---
    const handleAddMember = () => {
        if (!selectedUser) {
            toast.error('Please select a user to add.');
            return;
        }

        const isAlreadyMember = members?.some(m => m.profiles?.id === selectedUser.id);
        if (isAlreadyMember) {
             toast.error(`${selectedUser.username} is already a member of this shop.`);
             return;
        }

        addMember({
            shopId: shopId,
            userId: selectedUser.id,
            role: newMemberRole,
        }, {
            onSuccess: () => {
                setSelectedUser(null);
                setUserSearchTerm('');
            },
        });
    };

    // --- Member Removal Logic ---
    const openRemovalModal = (membershipId, username) => {
        setMemberToRemove({ membershipId, username });
        setIsRemovalModalOpen(true);
    };

    const handleConfirmRemoveMember = () => {
        if (!memberToRemove || isRemovingMember) return;

        removeMember(memberToRemove.membershipId, {
            onSuccess: () => {
                setIsRemovalModalOpen(false); 
                setMemberToRemove(null); 
            },
            onError: () => {
                setIsRemovalModalOpen(false); 
                setMemberToRemove(null); 
            }
        });
    };

    // Helper functions
    const formatRole = (role) => role ? role.charAt(0).toUpperCase() + role.slice(1) : '';
    
    const getRoleStyling = (role) => {
        switch (role) {
            case 'owner':
                return { text: 'bg-red-500 text-white', icon: <FaCrown color="#ffffff" size={12} className="inline mr-1" /> };
            case 'manager':
                return { text: 'bg-amber-500 text-white', icon: <FaUserShield color="#ffffff" size={12} className="inline mr-1" /> };
            case 'staff':
                return { text: 'bg-blue-500 text-white', icon: <FaUserTag color="#ffffff" size={12} className="inline mr-1" /> };
            default:
                return { text: 'bg-gray-200 text-gray-800', icon: null };
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const options = { year: 'numeric', month: 'short', day: 'numeric' };
            return new Date(dateString).toLocaleDateString(undefined, options);
        } catch (e) {
            return 'Invalid Date';
        }
    };

    return (
        <div className="max-w-6xl mx-auto p-3 sm:p-4 md:p-6 mb-20">
            
            {/* Header with Back Button */}
            <header className="relative bg-white shadow-md rounded-xl p-4 sm:p-5 md:p-6 mb-6 md:mb-10 border-t-4 border-indigo-600">
                
                {/* Back Button */}
                <button 
                    onClick={handleBack} 
                    className="absolute top-3.5 left-3.5 p-2 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-full transition duration-200 shadow-sm border border-gray-200 flex items-center justify-center"
                    title="Go Back"
                >
                    <FaArrowLeft color="#374151" size={16} />
                </button>

                <div className="flex flex-col sm:flex-row items-center pt-8 sm:pt-0 sm:pl-12 md:pl-0">
                    {shopLogoUrl ? (
                        <img 
                            src={shopLogoUrl} 
                            alt={`${shopName} logo`} 
                            className="w-16 h-16 sm:w-14 sm:h-14 md:w-16 md:h-16 object-cover rounded-full border-2 border-indigo-200 shadow-sm mb-3 sm:mb-0" 
                        />
                    ) : (
                        <div className="w-16 h-16 sm:w-14 sm:h-14 md:w-16 md:h-16 flex items-center justify-center bg-indigo-50 rounded-full border-2 border-indigo-100 mb-3 sm:mb-0">
                            <FaStore color="#4F46E5" size={26} />
                        </div>
                    )}
                    
                    <div className="sm:ml-4 text-center sm:text-left flex-1">
                        <h1 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-gray-900 tracking-tight leading-snug">
                            {shopName}
                        </h1>
                        <div className="mt-1 text-xs sm:text-sm md:text-base text-indigo-600 font-medium flex flex-wrap items-center justify-center sm:justify-start gap-1.5">
                            <span className="flex items-center">
                                <FaUserShield color="#4F46E5" size={14} className="mr-1.5" /> Team Management
                            </span>
                            {shopType && (
                                <span className="text-gray-500 text-xs bg-gray-100 px-2.5 py-0.5 rounded-full font-normal">
                                    {shopType}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* --- ADD MEMBER SECTION --- */}
            <div className="bg-white p-4 sm:p-6 md:p-8 rounded-xl shadow-md mb-8 border border-indigo-50">
                <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-indigo-700 mb-4 sm:mb-6 border-b pb-3 flex items-center">
                    <FaUserPlus color="#4F46E5" size={20} className="mr-2.5" /> Invite New Team Member
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                    
                    {/* User Search Input */}
                    <div className="col-span-1 md:col-span-2 relative">
                        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5">Search User by Username</label>
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Type username (min 2 characters)"
                                value={userSearchTerm}
                                onChange={(e) => {
                                    setUserSearchTerm(e.target.value);
                                    if (selectedUser && selectedUser.username !== e.target.value) {
                                        setSelectedUser(null);
                                    }
                                }}
                                className="w-full p-2.5 sm:p-3 pl-9 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                            />
                            <FaSearch color="#9CA3AF" size={14} className="absolute left-3 top-1/2 -translate-y-1/2" />
                        </div>

                        {/* Search Results Dropdown */}
                        {profiles.length > 0 && !selectedUser && (
                            <ul className="absolute z-30 w-full mt-1 border border-gray-200 rounded-lg max-h-48 overflow-y-auto bg-white shadow-xl ring-1 ring-black/5">
                                {profiles.map((profile) => (
                                    <li
                                        key={profile.id}
                                        className={`p-3 flex justify-between items-center cursor-pointer text-sm hover:bg-indigo-50 transition ${selectedUser?.id === profile.id ? 'bg-indigo-100 font-medium' : ''}`}
                                        onClick={() => {
                                            setSelectedUser(profile);
                                            setUserSearchTerm(profile.username); 
                                        }}
                                    >
                                        <span className="font-medium text-gray-800">{profile.username}</span>
                                        <FaUserPlus color="#4F46E5" size={14} />
                                    </li>
                                ))}
                            </ul>
                        )}

                        {selectedUser && (
                            <div className="mt-2 p-2.5 bg-green-50 text-green-700 rounded-lg border border-green-200 text-xs sm:text-sm">
                                <span className="font-bold">✅ Selected:</span> {selectedUser.username}
                            </div>
                        )}

                        {isSearchingProfiles && userSearchTerm.length >= 2 && (
                            <p className="mt-1.5 text-xs text-indigo-600 flex items-center">
                                <FaSpinner color="#4F46E5" size={12} className="animate-spin mr-1.5" /> Searching profiles...
                            </p>
                        )}
                    </div>

                    {/* Role Selection */}
                    <div className="col-span-1">
                        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5">Assign Role</label>
                        <select
                            value={newMemberRole}
                            onChange={(e) => setNewMemberRole(e.target.value)}
                            className="w-full p-2.5 sm:p-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white outline-none"
                        >
                            <option value="owner">Owner (Full Control)</option>
                            <option value="manager">Manager (Admin Tasks)</option>
                            <option value="staff">Staff (Basic Operations)</option>
                        </select>
                    </div>

                    {/* Submit Button */}
                    <div className="col-span-1">
                        <button
                            onClick={handleAddMember}
                            disabled={!selectedUser || isAddingMember}
                            className={`w-full py-2.5 sm:py-3 text-sm text-white font-bold rounded-lg transition duration-200 flex items-center justify-center shadow ${
                                !selectedUser || isAddingMember
                                    ? 'bg-gray-300 cursor-not-allowed text-gray-500'
                                    : 'bg-indigo-600 hover:bg-indigo-700 active:scale-[0.99]'
                            }`}
                        >
                            {isAddingMember ? (
                                <>
                                    <FaSpinner color="#ffffff" size={16} className="animate-spin mr-2" /> Adding...
                                </>
                            ) : (
                                <>
                                    <FaUserPlus color="#ffffff" size={16} className="mr-2" /> Add Member
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* --- EXISTING MEMBERS SECTION --- */}
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <FaUserShield color="#10B981" size={20} className="mr-2" /> Current Team Members ({members?.length || 0})
            </h2>

            {isLoadingMembers && (
                <div className="text-center py-8 text-gray-500 text-sm">
                    <FaSpinner color="#4F46E5" size={20} className="animate-spin inline mr-2" /> Loading team members...
                </div>
            )}
            
            {isMembersError && (
                <p className="text-red-500 text-sm p-4 bg-red-50 rounded-lg border border-red-100">
                    Error loading members: {membersError?.message || 'Unknown error'}
                </p>
            )}

            {/* MOBILE VIEW: List of Cards */}
            {members && members.length > 0 && (
                <div className="block md:hidden space-y-3">
                    {members.map((member) => {
                        const roleStyle = getRoleStyling(member.role);
                        const isCopied = copiedPhoneId === member.id;
                        const profile = member.profiles || {};

                        return (
                            <div key={member.id} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col space-y-3">
                                
                                {/* Top Row: Profile Picture, Name, Role & Action */}
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                        {profile.profile_picture ? (
                                            <img 
                                                src={profile.profile_picture} 
                                                alt={profile.username} 
                                                className="w-12 h-12 rounded-full object-cover border-2 border-indigo-100 shadow-sm"
                                            />
                                        ) : (
                                            <div className="w-12 h-12 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center">
                                                <FaUser color="#6366F1" size={20} />
                                            </div>
                                        )}
                                        <div>
                                            <h3 className="font-bold text-gray-900 text-sm leading-snug">{profile.username || 'Unknown User'}</h3>
                                            <span className={`mt-1 px-2.5 py-0.5 inline-flex text-[10px] font-bold rounded-full shadow-xs ${roleStyle.text}`}>
                                                {roleStyle.icon}
                                                {formatRole(member.role)}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Action: Remove or Sole Owner */}
                                    <div>
                                        {member.role !== 'owner' || members.filter(m => m.role === 'owner').length > 1 ? (
                                            <button
                                                onClick={() => openRemovalModal(member.id, profile.username)}
                                                className="p-2 rounded-full text-red-600 hover:bg-red-50 transition active:scale-95"
                                                disabled={isRemovingMember}
                                                title={`Remove ${profile.username}`}
                                            >
                                                <FaTrashAlt color="#EF4444" size={16} />
                                            </button>
                                        ) : (
                                            <span className="text-gray-400 text-[10px] px-2 py-0.5 bg-gray-100 rounded font-medium">Sole Owner</span>
                                        )}
                                    </div>
                                </div>

                                {/* Bottom Row: Clickable Phone Number & Member Since */}
                                <div className="pt-2 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                                    {profile.phone_number ? (
                                        <button
                                            onClick={() => handleCopyPhone(profile.phone_number, member.id)}
                                            className="flex items-center space-x-1.5 bg-gray-50 hover:bg-indigo-50 px-2.5 py-1.5 rounded-lg border border-gray-200 transition active:scale-95"
                                        >
                                            <FaPhone color="#4F46E5" size={11} />
                                            <span className="font-medium text-gray-700">{profile.phone_number}</span>
                                            {isCopied ? (
                                                <FaCheck color="#10B981" size={12} className="ml-1" />
                                            ) : (
                                                <FaCopy color="#9CA3AF" size={11} className="ml-1" />
                                            )}
                                        </button>
                                    ) : (
                                        <span className="text-gray-400 italic">No phone provided</span>
                                    )}

                                    <span className="text-gray-400 text-[11px]">
                                        Joined {formatDate(member.created_at)}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* DESKTOP VIEW: Table */}
            {members && members.length > 0 && (
                <div className="hidden md:block overflow-x-auto shadow-md rounded-xl border border-gray-100 bg-white">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">User</th>
                                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Email</th>
                                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Phone</th>
                                <th className="px-6 py-3.5 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Role</th>
                                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Member Since</th>
                                <th className="px-6 py-3.5 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Action</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                            {members.map((member) => {
                                const roleStyle = getRoleStyling(member.role);
                                const profile = member.profiles || {};
                                const isCopied = copiedPhoneId === member.id;

                                return (
                                    <tr key={member.id} className="hover:bg-indigo-50/50 transition duration-150">
                                        
                                        {/* User & Avatar */}
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            <div className="flex items-center space-x-3">
                                                {profile.profile_picture ? (
                                                    <img 
                                                        src={profile.profile_picture} 
                                                        alt={profile.username} 
                                                        className="w-9 h-9 rounded-full object-cover border border-indigo-100"
                                                    />
                                                ) : (
                                                    <div className="w-9 h-9 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center">
                                                        <FaUser color="#6366F1" size={16} />
                                                    </div>
                                                )}
                                                <span>{profile.username || 'N/A'}</span>
                                            </div>
                                        </td>

                                        {/* Email */}
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{profile.email || 'N/A'}</td>

                                        {/* Phone Number */}
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                            {profile.phone_number ? (
                                                <button
                                                    onClick={() => handleCopyPhone(profile.phone_number, member.id)}
                                                    className="inline-flex items-center space-x-1.5 bg-gray-50 hover:bg-indigo-50 px-2.5 py-1 rounded border border-gray-200 transition"
                                                >
                                                    <FaPhone color="#4F46E5" size={12} />
                                                    <span>{profile.phone_number}</span>
                                                    {isCopied ? <FaCheck color="#10B981" size={12} /> : <FaCopy color="#9CA3AF" size={11} />}
                                                </button>
                                            ) : (
                                                <span className="text-gray-400 italic">None</span>
                                            )}
                                        </td>

                                        {/* Role Badge */}
                                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                                            <span className={`px-3 py-1 inline-flex text-xs leading-5 font-bold rounded-full shadow-xs ${roleStyle.text}`}>
                                                {roleStyle.icon}
                                                {formatRole(member.role)}
                                            </span>
                                        </td>

                                        {/* Member Since */}
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatDate(member.created_at)}
                                        </td>

                                        {/* Remove Action */}
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            {member.role !== 'owner' || members.filter(m => m.role === 'owner').length > 1 ? (
                                                <button
                                                    onClick={() => openRemovalModal(member.id, profile.username)}
                                                    className="text-red-600 hover:text-red-800 transition p-2 rounded-full hover:bg-red-50 disabled:opacity-50"
                                                    disabled={isRemovingMember}
                                                    title={`Remove ${profile.username}`}
                                                >
                                                    <FaTrashAlt color="#EF4444" size={16} />
                                                </button>
                                            ) : (
                                                <span className="text-gray-400 text-xs px-2 py-1 bg-gray-100 rounded">Sole Owner</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {!isLoadingMembers && (!members || members.length === 0) && (
                <p className="text-gray-500 py-4 p-4 text-sm bg-gray-50 rounded-lg text-center">
                    There are no other members in this shop besides you.
                </p>
            )}

            {/* Removal Confirmation Modal */}
            <ConfirmationModal
                isOpen={isRemovalModalOpen}
                onCancel={() => setIsRemovalModalOpen(false)}
                onConfirm={handleConfirmRemoveMember}
                title="Confirm Member Removal"
                message={`You are about to remove **${memberToRemove?.username}** from the team. They will lose all access to **${shopName}**. Are you sure?`}
                confirmText={'Yes, Remove'}
                isPending={isRemovingMember}
            />
        </div>
    );
};

export default AddShopMemberPage;